import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { Bot, Check } from 'lucide-react';

export type AiEngine = 'claude' | 'devin' | 'deepagent';

export const AI_ENGINE_OPTIONS: { value: AiEngine; label: string; description: string }[] = [
  { value: 'claude', label: 'Claude', description: 'Default — Anthropic Claude via AWS Bedrock' },
  // { value: 'devin', label: 'Devin', description: 'Devin CLI for AI-powered code analysis' },
  // { value: 'deepagent', label: 'DeepAgent', description: 'Azure OpenAI for analysis' },
];

interface ScanEngineDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (agentMode: AiEngine) => void;
  title: string;
  description?: string;
  defaultEngine?: AiEngine;
  accentColor?: string;
  confirmLabel?: string;
}

export default function ScanEngineDialog({
  open,
  onClose,
  onConfirm,
  title,
  description = 'Choose the AI engine for this scan. Falls back to project default if the selected engine is not configured.',
  defaultEngine = 'claude',
  accentColor = '#3b82f6',
  confirmLabel = 'Start Scan',
}: ScanEngineDialogProps) {
  const theme = useTheme();
  const [selectedEngine, setSelectedEngine] = React.useState<AiEngine>(defaultEngine);

  React.useEffect(() => {
    if (open) setSelectedEngine(defaultEngine);
  }, [open, defaultEngine]);

  const handleConfirm = () => {
    onConfirm(selectedEngine);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              p: 0.75,
              borderRadius: 1.5,
              bgcolor: alpha(accentColor, 0.1),
              display: 'flex',
            }}
          >
            <Bot size={18} color={accentColor} />
          </Box>
          <Typography variant="h6" fontWeight={600}>
            {title}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {description}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 1, pb: 1.5 }}>
        <Stack spacing={1}>
          {AI_ENGINE_OPTIONS.map((opt) => {
            const isSelected = selectedEngine === opt.value;
            const isDefault = opt.value === defaultEngine;
            return (
              <Box
                key={opt.value}
                onClick={() => setSelectedEngine(opt.value)}
                sx={{
                  px: 2,
                  py: 1.25,
                  borderRadius: 2,
                  cursor: 'pointer',
                  border: '1.5px solid',
                  borderColor: isSelected ? accentColor : 'divider',
                  bgcolor: isSelected ? alpha(accentColor, 0.06) : 'background.paper',
                  transition: 'all 0.15s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  '&:hover': {
                    borderColor: isSelected ? accentColor : alpha(accentColor, 0.4),
                    bgcolor: alpha(accentColor, isSelected ? 0.06 : 0.03),
                  },
                }}
              >
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    border: '2px solid',
                    borderColor: isSelected ? accentColor : 'divider',
                    bgcolor: isSelected ? accentColor : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {isSelected && <Check size={10} color="#fff" strokeWidth={3} />}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                      {opt.label}
                    </Typography>
                    {isDefault && (
                      <Box
                        sx={{
                          px: 0.75,
                          py: 0.1,
                          borderRadius: 0.75,
                          bgcolor: alpha(theme.palette.text.secondary, 0.08),
                          fontSize: 9,
                          fontWeight: 700,
                          color: 'text.secondary',
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          lineHeight: 1.6,
                        }}
                      >
                        project default
                      </Box>
                    )}
                  </Stack>
                  <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.1 }}>
                    {opt.description}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disableElevation
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            background: `linear-gradient(135deg, ${accentColor} 0%, ${accentColor}cc 100%)`,
            '&:hover': { background: `linear-gradient(135deg, ${accentColor}dd 0%, ${accentColor} 100%)` },
          }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

